import type { Ref } from "vue";

type UseMobileSheetInteractionsOptions = {
  visible: Ref<boolean>;
  onClose: () => void;
  closeThreshold?: number;
  lockBodyScroll?: boolean;
};

export const useMobileSheetInteractions = ({
  visible,
  onClose,
  closeThreshold = 78,
  lockBodyScroll = true,
}: UseMobileSheetInteractionsOptions) => {
  const dragState = reactive({
    isDragging: false,
    startY: 0,
    deltaY: 0,
  });

  const resetDrag = () => {
    dragState.isDragging = false;
    dragState.startY = 0;
    dragState.deltaY = 0;
  };

  const sheetInlineStyle = computed(() => {
    if (!dragState.isDragging || dragState.deltaY <= 0) return {};
    return { transform: `translateY(${dragState.deltaY}px)` };
  });

  const onTouchStart = (event: TouchEvent) => {
    if (!visible.value) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    dragState.isDragging = true;
    dragState.startY = touch.clientY;
    dragState.deltaY = 0;
  };

  const onTouchMove = (event: TouchEvent) => {
    if (!dragState.isDragging) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    const delta = Math.max(0, touch.clientY - dragState.startY);
    dragState.deltaY = delta * 0.95;
  };

  const onTouchEnd = () => {
    if (!dragState.isDragging) return;
    const shouldClose = dragState.deltaY >= closeThreshold;
    resetDrag();
    if (shouldClose) onClose();
  };

  const onTouchCancel = () => {
    resetDrag();
  };

  watch(visible, (isVisible) => {
    if (!process.client) return;

    if (!isVisible) resetDrag();

    if (!lockBodyScroll) return;
    document.body.style.overflow = isVisible ? "hidden" : "";
  });

  onUnmounted(() => {
    if (!process.client || !lockBodyScroll) return;
    document.body.style.overflow = "";
  });

  return {
    dragState,
    sheetInlineStyle,
    resetDrag,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
  };
};
